import axios from 'axios';
import { makeObservable, observable, runInAction, when } from 'mobx';
import {
    ITenant,
    TenantStatus,
    IPartnerOverview,
    TenantManagementApi,
} from '@servicetitan/marketing-tenant-management-api';
import { DataResult, State } from '@servicetitan/data-query';
import { inject, injectable } from '@servicetitan/react-ioc';
import { AsyncDataSource, TableState } from '@servicetitan/table';
import { TABLE_PAGE_SIZE } from '../utils/ux';
import { LoadStatus } from '../../common/enums/load-status';
import { getInitialTableState } from '../utils/get-initial-table-state';
import { TenantsQueryParams } from '../utils/tenants-query-params-handler';

@injectable()
export class TenantsStore {
    @observable partners: IPartnerOverview[] = [];
    @observable tableState = new TableState<ITenant>({
        pageSize: TABLE_PAGE_SIZE,
    });
    @observable tenantsLoadStatus = LoadStatus.None;
    @observable partnersLoadStatus = LoadStatus.None;
    @observable searchFilter = '';
    @observable statusesFilter: TenantStatus[] = [];
    @observable partnersFilter: IPartnerOverview['id'][] = [];
    @observable queryParams?: TenantsQueryParams;

    abortController?: AbortController;

    constructor(
        @inject(TenantManagementApi) private readonly tenantManagementApi: TenantManagementApi,
    ) {
        makeObservable(this);
    }

    init = async (initialQueryParams: TenantsQueryParams) => {
        runInAction(() => {
            this.searchFilter = initialQueryParams.search ?? '';
            this.statusesFilter = initialQueryParams.statuses ?? [];
            this.partnersFilter = initialQueryParams.partners ?? [];
        });

        await Promise.all([
            this.fetchPartners(),
            this.tableState.setDataSource(new AsyncDataSource({ get: this.fetchTenants }), {
                initialState: {
                    ...getInitialTableState(),
                    skip: Math.floor(((initialQueryParams.page ?? 1) - 1) * TABLE_PAGE_SIZE),
                    sort: initialQueryParams.sortField
                        ? [
                              {
                                  dir: initialQueryParams.isAscending ? 'asc' : 'desc',
                                  field: initialQueryParams.sortField,
                              },
                          ]
                        : [],
                },
            }),
        ]);
    };

    fetchPartners = async () => {
        try {
            runInAction(() => {
                this.partnersLoadStatus = LoadStatus.Loading;
            });

            const { data: partners } = await this.tenantManagementApi.allAll();

            runInAction(() => {
                this.partners = partners;
                this.partnersLoadStatus = LoadStatus.Ok;
            });
        } catch {
            runInAction(() => {
                this.partnersLoadStatus = LoadStatus.Error;
            });
        }
    };

    fetchTenants = async (state: State): Promise<DataResult<ITenant>> => {
        const search = this.searchFilter;
        const statuses = this.statusesFilter;
        const partners = this.partnersFilter;

        this.abortController?.abort();
        await when(() => this.tenantsLoadStatus !== LoadStatus.Loading);
        this.abortController = new AbortController();

        const prevLoadStatus = this.tenantsLoadStatus;

        try {
            runInAction(() => {
                this.tenantsLoadStatus = LoadStatus.Loading;
            });

            const skip = state.skip ?? 0;
            const page = Math.floor(skip / TABLE_PAGE_SIZE);

            let sortField: string | undefined;
            let isAscending: boolean | undefined;

            if (state.sort?.[0]) {
                sortField = state.sort[0].field;
                isAscending = state.sort[0].dir === 'asc';
            }

            this.queryParams = {
                page: page + 1,
                search,
                statuses,
                partners,
                sortField,
                isAscending,
            };

            const {
                data: { tenants, tenantCount },
            } = await this.tenantManagementApi.all2(
                search,
                partners,
                statuses,
                page,
                state.take,
                isAscending,
                sortField,
                this.abortController?.signal,
            );

            runInAction(() => {
                this.tenantsLoadStatus = LoadStatus.Ok;
            });

            return {
                data: tenants || [],
                total: tenantCount,
            };
        } catch (error) {
            if (axios.isCancel(error)) {
                runInAction(() => {
                    this.tenantsLoadStatus = prevLoadStatus;
                });

                return {
                    data: this.tableState.data,
                    total: this.tableState.totalCount,
                };
            }

            runInAction(() => {
                this.tenantsLoadStatus = LoadStatus.Error;
            });

            return {
                data: [],
                total: 0,
            };
        }
    };

    filterBySearch = async (searchFilter: string) => {
        runInAction(() => {
            this.searchFilter = searchFilter;
        });

        await this.fetchTableDataFromStart();
    };

    filterByStatus = async (statusesFilter: typeof this.statusesFilter) => {
        runInAction(() => {
            this.statusesFilter = statusesFilter;
        });

        await this.fetchTableDataFromStart();
    };

    filterByPartner = async (partnersFilter: typeof this.partnersFilter) => {
        runInAction(() => {
            this.partnersFilter = partnersFilter;
        });

        await this.fetchTableDataFromStart();
    };

    resetFilters = async () => {
        runInAction(() => {
            this.searchFilter = '';
            this.statusesFilter = [];
            this.partnersFilter = [];
        });

        await this.fetchTableDataFromStart();
    };

    fetchTableDataFromStart = () => this.tableState.fetchData({ newSkip: 0 });
}
